import {
  type DayStatus,
  solarDayForUtcDate,
} from "@/lib/tool-kit/astronomy/solar";
import { dayNumber } from "@/lib/tool-kit/astronomy/zone";

export interface YearDay {
  /** Day number (days since 1970-01-01) of the calendar date. */
  day: number;
  daylightSeconds: number;
  status: DayStatus;
}

export interface YearSummary {
  days: YearDay[];
  longest: YearDay;
  shortest: YearDay;
  /** Days with the Sun up all day / never up. */
  polarDays: number;
  polarNights: number;
}

export function daysInYear(year: number): number {
  return dayNumber(year + 1, 1, 1) - dayNumber(year, 1, 1);
}

/**
 * Daylight on every day of a calendar year for a place. Daylight depends on latitude and
 * date only to within seconds, so the zone does not matter and each date is taken as the
 * solar day that belongs to that UTC date.
 */
export function yearDaylight(
  latitude: number,
  longitude: number,
  year: number
): YearSummary {
  const first = dayNumber(year, 1, 1);
  const count = daysInYear(year);
  const days: YearDay[] = [];
  for (let i = 0; i < count; i += 1) {
    const solar = solarDayForUtcDate(latitude, longitude, first + i);
    days.push({
      day: first + i,
      daylightSeconds: solar.daylightSeconds,
      status: solar.status,
    });
  }
  let longest = days[0];
  let shortest = days[0];
  for (const entry of days) {
    if (entry.daylightSeconds > longest.daylightSeconds) {
      longest = entry;
    }
    if (entry.daylightSeconds < shortest.daylightSeconds) {
      shortest = entry;
    }
  }
  return {
    days,
    longest,
    shortest,
    polarDays: days.filter((entry) => entry.status === "polar-day").length,
    polarNights: days.filter((entry) => entry.status === "polar-night").length,
  };
}

/** Day-of-year indexes (0-based) of the first of each month, for chart ticks. */
export function monthStarts(year: number): number[] {
  const first = dayNumber(year, 1, 1);
  const out: number[] = [];
  for (let month = 1; month <= 12; month += 1) {
    out.push(dayNumber(year, month, 1) - first);
  }
  return out;
}

/** Whole minutes and seconds of a signed span, for "+1 min 27 s". */
export function signedMinutesSeconds(seconds: number): {
  sign: "+" | "−" | "";
  minutes: number;
  seconds: number;
} {
  const rounded = Math.round(Math.abs(seconds));
  let sign: "+" | "−" | "" = "";
  if (seconds > 0.5) {
    sign = "+";
  } else if (seconds < -0.5) {
    sign = "−";
  }
  return { sign, minutes: Math.floor(rounded / 60), seconds: rounded % 60 };
}
